import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    AddTablePosition,
    EditTablePosition,
    STColumn, STData,
    TablePosition,
    TableSaveData,
    TableSumInfo,
} from '@app/interfaces/sys/st';
import { PermissionService } from '@services/sys/permission.service';
import { SettingsService } from '@services/sys/settings.service';
import { environment } from '@env/environment';

@Injectable({
    providedIn: 'root',
})

export class CommonService {
    private moduleApi = environment.api.services.sys;
    defaultResizeMinWidth = 60;
    stResizeMinWidth = 80;

    constructor(private settingsService: SettingsService,
                private permissionService: PermissionService,
                private http: HttpClient) {
    }

    getArrayMaxWidth(arr: any[], key: string, fontPixel: number = 14, offset: number = 0, formatter?: any): number {
        let maxWidth = 0;
        arr.forEach((obj) => {
            const columnIndent = obj?.columnIndent || 0;
            const str = formatter ? formatter(obj) : key ? (obj?.[key] || '') : obj;
            const width = this.settingsService.getTextWidth(str, offset + columnIndent, fontPixel);
            maxWidth = Math.max(maxWidth, width);
            // console.log('key', str, width);
        });
        return maxWidth;
    }

    getResourceNameRowWidth(): number {
        return this.getArrayMaxWidth(['w'], '') * 26;
    }

    getTableMaxWidth(columns: STColumn[], data: any[]): number {
        let fixedColumnWidth = 0;
        let autoColumnWidth = 0;
        columns.forEach(item => {
            const key = item?.index as string;
            if (item?.isAutoWidth) {
                autoColumnWidth += this.getArrayMaxWidth(data, key, 13);
                return;
            }
            fixedColumnWidth += parseInt(item.width as string);
        });
        return autoColumnWidth + fixedColumnWidth + 50;
    }

    getModalMaxWidth(minModalWidth: number, contentWidth: number, modalPadding: number): number {
        const width = contentWidth + modalPadding;
        return width > minModalWidth ? width : minModalWidth;
    }

    getMultipleMaxWidth(arr: any[], multipleFormat: any, fontPixel: number = 14, offset: number = 0): number {
        let maxWidth = 0;
        arr.forEach((obj) => {
            const strArr = multipleFormat(obj);
            const rowMaxWidth = strArr.length > 0 ? Math.max(...strArr.map((str: string) => this.settingsService.getTextWidth(str, offset, fontPixel))) : 0;
            maxWidth = Math.max(maxWidth, rowMaxWidth);
        });
        return maxWidth;
    }

    getFormDialogWidth(valueMaxWidth: number, valueSpan: number = 19, dialogPadding: number = 100): number {
        return (valueMaxWidth / valueSpan) * 24 + dialogPadding;
    }

    getInputMaxWidth(str: string): number {
        return this.settingsService.getTextWidth(str, 0) + 25;
    }

    getOptionWidth(arr: any[], key: string = 'label', defaultWidth: number = 150): number {
        return Math.max(this.getArrayMaxWidth(arr, key) + 75, defaultWidth);
    }

    async getTablePosition(type: string): Promise<any> {
        return this.http.get<any>(`${this.moduleApi}/user-position`, {
            params: {
                type,
            },
        }).toPromise();
    }

    async addTablePosition(data: AddTablePosition): Promise<any> {
        return this.http.put<any>(`${this.moduleApi}/user-position`, data).toPromise();
    }

    async editTablePosition(data: EditTablePosition): Promise<any> {
        return this.http.post<any>(`${this.moduleApi}/user-position`, data).toPromise();
    }

    formatSavedData(tablePositions: TablePosition): TableSaveData {
        const positions = JSON.parse(tablePositions?.positions || '{}');
        const { columns, size } = positions;
        const obj = this.getColumnObj(columns);
        return {
            size,
            positions,
            formatterColumnsObj: obj,
        };
    }

    getColumnObj(columns: STColumn[]): any {
        let obj = {};
        (columns || []).forEach((it: STColumn, i) => {
            const { index, title } = it;
            const key = index || title || i;
            obj = {
                ...obj,
                [key as string]: it?.realWidth || it?.width,
            };
        });
        return obj;
    }

    resizeColumn(data: any): any {
        const { columns, resizeIndex, resizedWidth, tableWidth } = data;
        // 拖拽的是否是最后一列
        const lastIndex = columns.length - 1 as number;

        const resizedItem = columns[resizeIndex];
        // 1. 先应用拖拽结果
        resizedItem.realWidth = `${resizedWidth}px`;
        // 2. 计算当前总宽
        const totalNow = columns.reduce((sum: number, col: STColumn) => sum + parseFloat(String(col.realWidth || col.width || '0')), 0);

        // 3. 只有「总宽 < tableWidth」才补偿
        if (totalNow < tableWidth) {
            const deficit = tableWidth - totalNow; // 需要补的像素

            if (resizeIndex === lastIndex && columns.length > 1) {
                // 最后一列自己被拖 → 让倒数第二列补
                const prevCol = columns[lastIndex - 1];
                const prevWidth = parseFloat(prevCol.realWidth || prevCol.width || '0');
                prevCol.realWidth = `${prevWidth + deficit}px`;
            } else if (resizeIndex !== lastIndex) {
                // 非最后一列被拖 → 最后一列补
                const lastCol = columns[lastIndex];
                const lastWidth = parseFloat(lastCol.realWidth || lastCol.width || '0');
                lastCol.realWidth = `${lastWidth + deficit}px`;
            }
            // 只剩一列时允许总宽变小，不做处理
        }
        return columns;
    }

    getTableColumnRealWidth(columns: STColumn[], tableWidth: number, tableColumnObj?: {
        [columnKey: string]: string
    }): {
        columns: STColumn[],
        lastColumnIndex: number
    } {
        let allWidth = 0;
        let autoWidth = 0;
        let lastColumnIndex!: number;
        let formatterColumns: STColumn[] = columns;
        const hasSavedColumns = tableColumnObj && JSON.stringify(tableColumnObj) !== '{}';
        const allHasWidth = formatterColumns.every(it => it?.realWidth || it?.width);
        if (allHasWidth) {
            let beforeColumnObj: {
                [key: string]: string
            } = {};
            formatterColumns?.forEach((it, index) => {
                const showColumn = !it.iif || it.iif(it);
                const key = it?.index || it?.title as string;
                const savedWidth = tableColumnObj?.[key] || it.width as string;
                const autoWidthColumn = it?.isAutoWidth || false;
                beforeColumnObj = {
                    ...beforeColumnObj,
                    [key]: savedWidth,
                };
                if (!showColumn) {
                    return;
                }
                lastColumnIndex = index;
                const columnWidth = parseInt(savedWidth as string) || 0;
                if (!hasSavedColumns && autoWidthColumn) {
                    autoWidth += columnWidth;
                    return;
                }
                allWidth += columnWidth;
            });
            formatterColumns = formatterColumns.map(it => {
                const key = it?.index || it?.title as string;
                const columnWidth = parseInt(beforeColumnObj?.[key] || it?.width as string);
                const minWidth = it?.dragMinWidth || this.stResizeMinWidth;
                const autoWidthColumn = it?.isAutoWidth || false;
                const width = hasSavedColumns || (!hasSavedColumns && !autoWidthColumn) ? (columnWidth / allWidth) * (tableWidth - autoWidth) : columnWidth;
                const realWidth = `${Math.max(minWidth, width)}px`;
                return {
                    ...it,
                    width: realWidth,
                    realWidth,
                };
            });
        }
        return {
            columns: formatterColumns,
            lastColumnIndex,
        };
    }

    // 获取表格的宽度
    getColumnWidth(it: STColumn, tableColumnObj?: { [columnKey: string]: string }, columnIndex?: number): number {
        const columnKey = it?.index || it?.title || (columnIndex || 0);
        const columnWidth = parseFloat(tableColumnObj?.[columnKey] || it.width as string) || 60;
        return columnWidth;
    }


    /*width: The initial version is used to control the proportion occupied by table-column
    realWidth: Increases when drag-and-drop functionality is added, storing the actual width of table-columns
    isAutoWidth: Whether the width is automatically expanded, specifically for the resourceName column in the table (the longest resourceName on the current page can span at most two rows).
    dragMinWidth: Due to the presence of autoWidth above, there is a minimum drag width
    titleWidth: Add drag-and-drop functionality. Try not to drag the title to a new line, hence the title width
    minWidth: For certain special columns, a minimum width needs to be set.*/
    formatTableRealWidth(
        columns: STColumn[],
        tableWidth: number,
        tableColumnObj: { [columnKey: string]: string },
        tableData: STData[]
    ): STColumn[] {
        const copyColumns = columns;
        // The length of the `resourceName` on the AIO side is controlled by a global variable, with a maximum of 50 characters, so half is 26
        // const limitWrapWidth = this.getResourceNameRowWidth();
        const allHasWidth = copyColumns.every(c => c.width);

        if (allHasWidth) {
            copyColumns.forEach((c, i) => {
                const { minWidth, title, isAutoWidth, sort, customFilter, nzFilterFn } = c;
                const columnKey = c?.index as string;
                // The width at which content will wrap to a new line
                let contentWrapWidth = 0;
                if (!c.iif || c.iif(c)) {
                    // Get the width of the cache
                    const w = this.getColumnWidth(c, tableColumnObj, i);
                    // Get the width of the title
                    const titleWidth = this.settingsService.getTextWidth(title || '');
                    if (isAutoWidth) {
                        const format = c.format;
                        const multipleFormat = c.multipleFormat;
                        const strMaxWidth = multipleFormat ? this.getMultipleMaxWidth(tableData, multipleFormat) :
                            this.getArrayMaxWidth(tableData, columnKey, 14, 0, format);
                        contentWrapWidth = strMaxWidth / 2 + 16;
                    }
                    // The last 20 is drag column reset
                    const dragMinWidth = Math.max(titleWidth, contentWrapWidth, (minWidth || 0)) + (sort ? 12 : 0) + (customFilter || nzFilterFn ? 20 : 0) + (i === 0 ? 20 : 0);
                    c.dragMinWidth = dragMinWidth;
                    c.titleWidth = titleWidth;
                    c.realWidth = `${Math.max(w, dragMinWidth)}px`;
                }
            });
            const visibleColumns = copyColumns.filter(c => !c.iif || c.iif(c));
            const realWidthSum = visibleColumns.reduce((acc, cur) => acc + parseFloat(String(cur.realWidth || cur.width || 0)), 0);
            const delta = tableWidth - realWidthSum;
            // console.log('delta: ', delta);
            // The content is stretched evenly without exceeding the width of the table
            if (delta > 0) {
                const avg = delta / visibleColumns.length;
                copyColumns.forEach(c => {
                    const visible = !c.iif || c.iif(c);
                    if (!visible) {
                        return;
                    }
                    const base = parseInt((c.realWidth || c.width) as string, 10) || 0;
                    const w = `${parseFloat(String(base + avg))}px`;
                    c.realWidth = w;
                });
            }
        }

        return copyColumns;
    }

    getTableLastColumnIndex(columns: any[]): number {
        let index = 0;
        columns.forEach((it, _idx) => {
            const showColumn = !it.iif || it.iif(it);
            if (!showColumn) {
                return;
            }
            index = _idx;
        });
        return index;
    }


    deepClone(obj: any): any {
        if (obj === null || typeof obj !== 'object') {
            return obj;
        }
        const clone = Array.isArray(obj) ? [] : {};
        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                if (typeof obj[key] === 'function') {
                    // @ts-ignore
                    clone[key] = obj[key].bind(clone);
                } else {
                    // @ts-ignore
                    clone[key] = this.deepClone(obj[key]);
                }
            }
        }
        return clone;
    }

    getContentWidth(columns: STColumn[], content: any[], tableWidth: number, indent: number = 0): STColumn[] {
        const limitWrapWidth = this.getResourceNameRowWidth();
        // 16 table padding
        columns.forEach(item => {
            // const columnKey = item?.index || item?.title as string;
            // const { title } = item;
            // let strMaxWidth = 0;
            // const titleWidth = this.settingsService.getTextWidth(title || '');
            // const defaultWidth = item?.defaultWidth ? parseInt(item?.defaultWidth as string) : 0;
            // // 可自动延伸的列字段宽度
            // if (item?.isAutoWidth) {
            //     const format = item.format;
            //     const multipleFormat = item.multipleFormat;
            //     strMaxWidth = multipleFormat ? this.getMultipleMaxWidth(content, multipleFormat) :
            //         this.getArrayMaxWidth(content, columnKey, 14, 0, format);
            // }
            // const contentWidth = Math.max(defaultWidth, strMaxWidth, this.stResizeMinWidth) + indent + 16;
            // item.contentWidth = contentWidth;
            // item.titleWidth = Math.max(titleWidth, defaultWidth);
            // item.dragMinWidth = (contentWidth < limitWrapWidth ? contentWidth : (Math.max(contentWidth / 2, limitWrapWidth)));
            // item.dragMaxWidth = item.isAutoWidth ? contentWidth * 2 : Math.max(tableWidth / 2, contentWidth * 2);
        });
        return columns;
    }
}
